import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  withStyles,
  WithStyles,
  Theme,
  createStyles,
  Box,
  Typography,
} from '@material-ui/core';
import TemplatePromptBar, {
  DEFAULT_TEMPLATE_COUNT,
  PromptSubmission,
} from './TemplatePromptBar';
import TemplateIdeasGallery from './TemplateIdeasGallery';
import GeneratedAdsSection from './GeneratedAdsSection';
import AdsAgentThemeProvider from './AdsAgentThemeProvider';
import { AD } from './adsAgentTokens';
import LeadCaptureDialog, { LeadDialogStep } from './LeadCaptureDialog';
import JoinAiSensyCard from './JoinAiSensyCard';
import adTemplatesResponse from './adTemplateIdeas';
import {
  getErrorCode,
  getErrorMessage,
  getResult,
  initiate,
  SIGNUP_URL,
  startGeneration,
  uploadImage,
  verifyOtp,
} from './adsGeneratorApi';
import { GeneratedTemplate, LeadDetails, StoredSession } from './adsGenerator.types';
import { clearSession, loadSession, saveSession } from './adsSession';

// Public Ads Agent page (ADS-266): prompt → lead + OTP → the Ads Agent job runs
// (rest-api → SQS → py-gen-ai-workers) → the page polls for progress and shows
// the ads in the console cards. The OTP session is kept in the browser until it
// expires (60 min), so a refresh shows the visitor's ads again; after that, a
// new OTP for the same phone shows them.

type PageView = 'form' | 'ads';

const RESULT_POLL_MS = 5000;
const IN_PROGRESS = ['pending', 'processing'];
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyLead: LeadDetails = {
  name: '',
  email: '',
  country: 'India',
  countryCode: '+91',
  phoneNumber: '',
  state: '',
};

// Shown between "generate" and the first result so the skeletons appear at once.
const queuedTemplate = (count: number): GeneratedTemplate => ({
  _id: 'queued',
  status: 'pending',
  templateCount: count,
  progress: { stage: 'Queued', percent: 0 },
  variants: [],
});

const validateLead = (value: LeadDetails): string => {
  if (!value.name.trim()) {
    return 'Please enter your name.';
  }
  if (!EMAIL_REGEX.test(value.email.trim())) {
    return 'Please enter a valid email address.';
  }
  if (!value.countryCode) {
    return 'Please select your country.';
  }
  if (value.phoneNumber.length < 6 || value.phoneNumber.length > 15) {
    return 'Please enter a valid WhatsApp number.';
  }
  if (!value.state.trim()) {
    return 'Please select your state.';
  }
  return '';
};

const AdsGeneratorPage = (props: Props): React.ReactElement => {
  const { classes } = props;

  const [view, setView] = useState<PageView>('form');
  const [submission, setSubmission] = useState<PromptSubmission | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogStep, setDialogStep] = useState<LeadDialogStep>('details');
  const [lead, setLead] = useState<LeadDetails>(emptyLead);
  const [otp, setOtp] = useState('');
  const [phone, setPhone] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [otpChannels, setOtpChannels] = useState<string[]>([]);

  const [template, setTemplate] = useState<GeneratedTemplate | null>(null);
  // This phone's previous generations, newest first, shown under `template`.
  const [earlierTemplates, setEarlierTemplates] = useState<GeneratedTemplate[]>([]);
  const [waMessageSent, setWaMessageSent] = useState(false);
  const [generationError, setGenerationError] = useState('');
  // Set once this phone has used its free generation (until refresh).
  const [limitReached, setLimitReached] = useState(false);
  // True while the visitor's own ads are being loaded (saved session check, or
  // right after OTP), so the static examples don't flash before them.
  const [restoring, setRestoring] = useState(() => loadSession() !== null);

  const pollTimer = useRef<number | null>(null);
  // Mirrors the session saved in the browser (or the only copy when storage is blocked).
  const sessionRef = useRef<StoredSession | null>(null);
  // Product image id -> uploaded URL, so a retry does not upload twice.
  const uploadedUrls = useRef<Map<string, string>>(new Map());

  const stopPolling = () => {
    if (pollTimer.current) {
      window.clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  };

  // A failed generation keeps the visitor on the normal page (prompt box and
  // example cards) and shows the message above the cards.
  const fail = (message: string) => {
    stopPolling();
    setGenerationError(message);
    setTemplate(null);
    setView('form');
  };

  // One poll: shows progress/ads and stops once the job is no longer running.
  const refreshResult = useCallback(async (session: StoredSession): Promise<boolean> => {
    try {
      const result = await getResult(session.phone, session.sessionToken);
      setWaMessageSent(result.waMessageSent);
      if (result.generationsLeft !== undefined) {
        setLimitReached(result.generationsLeft <= 0);
      }
      if (!result.template) {
        if (result.status === 'failed') {
          fail('We could not generate your ads. Please try again.');
        }
        return false;
      }
      setTemplate(result.template);
      setEarlierTemplates((result.templates || []).slice(1));
      setView('ads');
      const running = IN_PROGRESS.includes(result.template.status);
      if (!running) {
        stopPolling();
        if (result.template.status === 'failed') {
          fail('We could not generate your ads. Please try again.');
        }
      }
      return running;
    } catch (e) {
      if (getErrorCode(e) === 'SESSION_INVALID') {
        clearSession();
        sessionRef.current = null;
      }
      fail(getErrorMessage(e));
      return false;
    }
  }, []);

  const pollResult = useCallback(
    (session: StoredSession): Promise<void> => {
      stopPolling();
      return refreshResult(session).then((running) => {
        if (running) {
          pollTimer.current = window.setInterval(() => refreshResult(session), RESULT_POLL_MS);
        }
      });
    },
    [refreshResult],
  );

  useEffect(() => {
    document.title = 'FREE AI Ads Agent by AiSensy';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        "Generate ready-to-run ad creatives for your business in minutes with AiSensy's free AI Ads Agent",
      );
    }
    return stopPolling;
  }, []);

  // Back within the hour: show this visitor's ads again without a new OTP.
  // A session the server no longer accepts is dropped quietly.
  useEffect(() => {
    const saved = loadSession();
    if (!saved) {
      setRestoring(false);
      return;
    }
    sessionRef.current = saved;
    setPhone(saved.phone);
    getResult(saved.phone, saved.sessionToken)
      .then((result) => {
        if (result.template) {
          setTemplate(result.template);
          setEarlierTemplates((result.templates || []).slice(1));
          setWaMessageSent(result.waMessageSent);
          if (result.generationsLeft !== undefined) {
            setLimitReached(result.generationsLeft <= 0);
          }
          setView('ads');
          if (IN_PROGRESS.includes(result.template.status)) {
            pollResult(saved);
          }
        }
      })
      .catch(() => {
        clearSession();
        sessionRef.current = null;
        setPhone('');
      })
      .finally(() => setRestoring(false));
  }, [pollResult]);

  const activeSession = (): StoredSession | null => {
    const session = sessionRef.current;
    if (session && session.expiresAt > Date.now()) {
      return session;
    }
    return null;
  };

  const uploadPendingImages = async (
    session: StoredSession,
    images: PromptSubmission['productImages'],
  ): Promise<string[]> => {
    const urls: string[] = [];
    for (const image of images) {
      let url = uploadedUrls.current.get(image.id);
      if (!url) {
        url = await uploadImage(session.phone, session.sessionToken, image.file);
        uploadedUrls.current.set(image.id, url);
      }
      urls.push(url);
    }
    return urls;
  };

  // `next` is passed when a new prompt is submitted from an active session, so
  // the backend uses that prompt instead of the one saved at OTP time.
  const startAndPoll = async (session: StoredSession, next?: PromptSubmission) => {
    const current = next || submission;
    setGenerationError('');
    // The ads already on screen move down to "earlier" while the new ones load.
    if (template && template._id !== 'queued') {
      const previous = template;
      setEarlierTemplates((list) => [previous, ...list.filter((t) => t._id !== previous._id)]);
    }
    setTemplate(queuedTemplate(DEFAULT_TEMPLATE_COUNT));
    setView('ads');

    let productImageUrls: string[] = [];
    try {
      productImageUrls = await uploadPendingImages(session, current ? current.productImages : []);
    } catch (e) {
      fail(`We could not upload your images. ${getErrorMessage(e)}`);
      return;
    }

    try {
      await startGeneration(
        session.phone,
        session.sessionToken,
        productImageUrls,
        next ? { prompt: next.prompt, aspectRatio: next.aspectRatio, adType: next.adType } : undefined,
      );
    } catch (e) {
      // This number already used its free generation: show those ads.
      if (getErrorCode(e) !== 'LIMIT_REACHED') {
        fail(getErrorMessage(e));
        return;
      }
      // Limit used up (e.g. in another tab): ask them to sign up, and go back
      // to the ads they already have.
      setLimitReached(true);
      setDialogError('');
      setDialogStep('signup');
      setDialogOpen(true);
    }
    pollResult(session);
  };

  const handlePromptSubmit = (next: PromptSubmission) => {
    setGenerationError('');
    // Saved first: "Use a different number" in the signup popup sends this prompt.
    setSubmission(next);
    if (limitReached) {
      setDialogError('');
      setDialogStep('signup');
      setDialogOpen(true);
      return;
    }
    const session = activeSession();
    if (session) {
      startAndPoll(session, next);
      return;
    }
    setDialogError('');
    setDialogStep('details');
    setDialogOpen(true);
  };

  const sendOtp = async () => {
    // The backend validates the prompt, so a missing one gets its message.
    const response = await initiate({
      name: lead.name.trim(),
      email: lead.email.trim().toLowerCase(),
      phone: lead.countryCode.replace(/\D/g, '') + lead.phoneNumber,
      state: lead.state.trim(),
      prompt: submission ? submission.prompt : '',
      aspectRatio: submission ? submission.aspectRatio : '3:4',
      adType: submission ? submission.adType : 'auto',
      adCount: submission ? submission.templateCount : DEFAULT_TEMPLATE_COUNT,
    });
    uploadedUrls.current = new Map();
    setPhone(response.phone);
    setOtpChannels(response.otpChannels);
  };

  const handleSendOtp = async () => {
    const error = validateLead(lead);
    setDialogError(error);
    if (error) {
      return;
    }
    setIsSubmitting(true);
    try {
      await sendOtp();
      setOtp('');
      setDialogStep('otp');
    } catch (e) {
      setDialogError(getErrorMessage(e));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    setIsResending(true);
    setDialogError('');
    try {
      await sendOtp();
      setOtp('');
    } catch (e) {
      setDialogError(getErrorMessage(e));
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyOtp = async () => {
    setIsSubmitting(true);
    setDialogError('');
    try {
      const response = await verifyOtp(phone, otp);
      const session: StoredSession = {
        phone,
        sessionToken: response.sessionToken,
        expiresAt: Date.now() + response.expiresInSec * 1000,
      };
      sessionRef.current = session;
      saveSession(session);
      // A number that already generated (or is generating) sees that job;
      // otherwise this starts its free generation. The loader / skeletons are
      // set before the dialog closes, so the static examples never flash.
      if (response.templateId && (response.status === 'completed' || response.status === 'generating')) {
        if (response.generationsLeft <= 0 && response.status === 'completed') {
          setLimitReached(true);
        }
        setRestoring(true);
        pollResult(session).finally(() => setRestoring(false));
      } else {
        startAndPoll(session);
      }
      setDialogOpen(false);
      setIsSubmitting(false);
    } catch (e) {
      setDialogError(getErrorMessage(e));
      setIsSubmitting(false);
    }
  };

  const isGenerating = view === 'ads' && !!template && IN_PROGRESS.includes(template.status);

  return (
    <div className={classes.root}>
      <div className={classes.body}>
        <div className={classes.content}>
          <AdsAgentThemeProvider>
            <div className={classes.panel}>
              <TemplatePromptBar
                submitting={isGenerating}
                disabled={dialogOpen || isGenerating}
                onSubmit={handlePromptSubmit}
              />
            </div>

            {generationError && view === 'form' ? (
              <Box mb={2}>
                <Typography variant="body2" color="error" role="alert">
                  {generationError}
                </Typography>
              </Box>
            ) : null}

            {restoring && view === 'form' ? (
              <GeneratedAdsSection
                template={queuedTemplate(DEFAULT_TEMPLATE_COUNT)}
                waMessageSent={false}
                signupUrl={SIGNUP_URL}
              />
            ) : null}

            {view === 'form' && !restoring ? (
              <TemplateIdeasGallery templates={adTemplatesResponse.data} />
            ) : null}

            {view === 'ads' && limitReached ? <JoinAiSensyCard signupUrl={SIGNUP_URL} /> : null}

            {view === 'ads' && template ? (
              <GeneratedAdsSection
                template={template}
                earlierTemplates={earlierTemplates}
                waMessageSent={waMessageSent}
                signupUrl={SIGNUP_URL}
              />
            ) : null}

          </AdsAgentThemeProvider>

        </div>
      </div>

      <LeadCaptureDialog
        open={dialogOpen}
        step={dialogStep}
        details={lead}
        onDetailsChange={setLead}
        onSendOtp={handleSendOtp}
        otp={otp}
        onOtpChange={setOtp}
        onVerifyOtp={handleVerifyOtp}
        onResendOtp={handleResendOtp}
        onEditDetails={() => {
          // The limit belonged to the previous number.
          if (dialogStep === 'signup') {
            setLimitReached(false);
          }
          setDialogError('');
          setDialogStep('details');
        }}
        onClose={() => setDialogOpen(false)}
        isSubmitting={isSubmitting}
        isResending={isResending}
        error={dialogError}
        signupUrl={SIGNUP_URL}
        otpChannels={otpChannels}
        verifiedPhone={phone}
      />
    </div>
  );
};

interface Props extends WithStyles<typeof styles> {}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      minHeight: '100%',
      backgroundColor: AD.surfaceSunken,
      boxSizing: 'border-box',
    },
    body: {
      padding: '24px',
      boxSizing: 'border-box',
      [theme.breakpoints.down('xs')]: { padding: '16px 12px' },
    },
    content: {
      maxWidth: 1400,
      margin: '0 auto',
    },
    panel: {
      borderRadius: AD.radiusLg,
      overflow: 'hidden',
      marginBottom: 24,
    },
    heading: {
      fontWeight: 600,
    },
    whiteText: {
      color: theme.palette.common.white,
    },
  });

export default withStyles(styles)(AdsGeneratorPage);
